import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowDownLeft, ArrowUpLeft, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";

type GreenZone = 'bottom-left' | 'top-left' | 'bottom-right' | 'top-right';

interface GreenAttackScoreButtonsProps {
  onScoreChange: (score: number, tendency?: GreenZone) => void;
}

const GreenAttackScoreButtons: React.FC<GreenAttackScoreButtonsProps> = ({ onScoreChange }) => {
  const { t } = useTranslation('training');
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [showZone, setShowZone] = useState(false);

  const handleScoreClick = (score: number) => {
    if (score === 0) {
      onScoreChange(score);
      return;
    }
    
    setSelectedScore(score);
    setShowZone(true);
  };

  const handleZoneClick = (zone: GreenZone) => {
    if (selectedScore !== null) {
      onScoreChange(selectedScore, zone);
      resetSelection();
    }
  };

  const resetSelection = () => {
    setSelectedScore(null);
    setShowZone(false);
  };

  if (showZone) {
    return (
      <div className="space-y-3">
        <div className="text-center text-sm font-medium text-golf-700 mb-2">
          {t('interface.scoring.greenAttack.selectZone')}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => handleZoneClick('top-left')}
            className="h-16 bg-blue-500 hover:bg-blue-600 text-white font-semibold flex flex-col items-center justify-center gap-1"
          >
            <ArrowUpLeft className="h-5 w-5" />
            <span className="text-xs">{t('interface.scoring.greenAttack.topLeft')}</span>
          </Button>
          <Button
            onClick={() => handleZoneClick('top-right')}
            className="h-16 bg-blue-600 hover:bg-blue-700 text-white font-semibold flex flex-col items-center justify-center gap-1"
          >
            <ArrowUpRight className="h-5 w-5" />
            <span className="text-xs">{t('interface.scoring.greenAttack.topRight')}</span>
          </Button>
          <Button
            onClick={() => handleZoneClick('bottom-left')}
            className="h-16 bg-green-500 hover:bg-green-600 text-white font-semibold flex flex-col items-center justify-center gap-1"
          >
            <ArrowDownLeft className="h-5 w-5" />
            <span className="text-xs">{t('interface.scoring.greenAttack.bottomLeft')}</span>
          </Button>
          <Button
            onClick={() => handleZoneClick('bottom-right')}
            className="h-16 bg-green-600 hover:bg-green-700 text-white font-semibold flex flex-col items-center justify-center gap-1"
          >
            <ArrowDownRight className="h-5 w-5" />
            <span className="text-xs">{t('interface.scoring.greenAttack.bottomRight')}</span>
          </Button>
        </div>
        <Button
          onClick={resetSelection}
          variant="outline"
          className="w-full"
        >
          {t('interface.scoring.greenAttack.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Button
        onClick={() => handleScoreClick(10)}
        className="w-full h-14 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold text-lg shadow-md"
      >
        {t('interface.scoring.ballIn')} (10 {t('interface.scoring.points')})
      </Button>
      <Button
        onClick={() => handleScoreClick(8)}
        className="w-full h-14 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white font-semibold text-lg shadow-md"
      >
        {t('interface.scoring.ballAt')} 1m (8 {t('interface.scoring.points')})
      </Button>
      <Button
        onClick={() => handleScoreClick(6)}
        className="w-full h-14 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white font-semibold text-lg shadow-md"
      >
        {t('interface.scoring.ballAt')} 3m (6 {t('interface.scoring.points')})
      </Button>
      <Button
        onClick={() => handleScoreClick(3)}
        className="w-full h-14 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-white font-semibold text-lg shadow-md"
      >
        {t('interface.scoring.ballAt')} 5m (3 {t('interface.scoring.points')})
      </Button>
      <Button
        onClick={() => handleScoreClick(0)}
        className="w-full h-14 bg-gradient-to-r from-red-400 to-red-500 hover:from-red-500 hover:to-red-600 text-white font-semibold text-lg shadow-md"
      >
        {t('interface.scoring.ballOutside')} (0 {t('interface.scoring.point')})
      </Button>
    </div>
  );
};

export default GreenAttackScoreButtons;
